import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  type CaseData,
  type EvidenceBucket,
  GameStatus,
  type Item,
  type ItemWithScene,
} from '../../types';
import {
  createLocationEvidenceBucket,
  findNextActiveClue,
} from '../../utils/evidenceBucket';

interface GameState {
  gameStatus: GameStatus;
  caseData: CaseData | null;
  error: string | null;
  loadingMessage: string;

  gameStartTime: number;

  currentLocationIndex: number;
  currentSceneIndex: number;
  pendingSceneNavigation: number | null;

  evidenceBucket: EvidenceBucket | null;

  foundItems: Item[];
  wrongClicks: number;
  wrongClickTimestamps: number[];

  hasSeenSwipeTutorial: boolean;

  levelEndState: {
    shouldEndLevel: boolean;
    endReason: 'evidence_complete' | 'manual_bail' | null;
    timeRemaining: number;
    clearedByEvidence: boolean;
  };
}

const initialState: GameState = {
  gameStatus: GameStatus.TITLE,
  caseData: null,
  error: null,
  loadingMessage: '',
  gameStartTime: null,
  currentLocationIndex: 0,
  currentSceneIndex: 0,
  pendingSceneNavigation: null,
  evidenceBucket: null,
  foundItems: [],
  wrongClicks: 0,
  wrongClickTimestamps: [],
  hasSeenSwipeTutorial: false,
  levelEndState: {
    shouldEndLevel: false,
    endReason: null,
    timeRemaining: 0,
    clearedByEvidence: false,
  },
};

export const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    // Initialization
    resetGameState: () => initialState,
    initializeGame: (_state, action: PayloadAction<CaseData>) => {
      return {
        ...initialState,
        caseData: action.payload,
        currentLocationIndex: 0,
        loadingMessage: '',
        gameStatus: GameStatus.VIDEO_BEFORE_LOCATION,
      };
    },
    initializeLocation: (
      state,
      {
        payload: { skipStatusChange } = { skipStatusChange: false },
      }: PayloadAction<{ skipStatusChange: boolean }>,
    ) => {
      return {
        ...state,
        currentSceneIndex: 0,
        wrongClicks: 0,
        wrongClickTimestamps: [],
        pendingSceneNavigation: null,
        ...(skipStatusChange && { gameStatus: GameStatus.GAMEPLAY }),
      };
    },

    setLoadingMessage: (state, action: PayloadAction<string>) => ({
      ...state,
      loadingMessage: action.payload,
    }),
    // Game status and navigation
    setGameStatus: (state, action: PayloadAction<GameStatus>) => ({
      ...state,
      gameStatus: action.payload,
    }),

    setCurrentLocationIndex: (state, action: PayloadAction<number>) => ({
      ...state,
      currentLocationIndex: action.payload,
    }),

    // Tutorial
    setSwipeTutorialComplete: (state) => ({
      ...state,
      hasSeenSwipeTutorial: true,
    }),

    // Mis-clicks
    setWrongClick: (state) => {
      const timestamp = Date.now();

      return {
        ...state,
        wrongClickTimestamps: [...state.wrongClickTimestamps, timestamp],
        wrongClicks: state.wrongClicks + 1,
      };
    },

    // Evidence buckets
    initializeLocationEvidenceBucket: (state) => {
      const newEvidenceBucket = createLocationEvidenceBucket(
        state.caseData.locations[state.currentLocationIndex],
        state.currentLocationIndex,
      );
      return {
        ...state,
        evidenceBucket: newEvidenceBucket,
      };
    },

    markEvidenceAsFound: (
      state,
      action: PayloadAction<{ foundItem: ItemWithScene }>,
    ) => {
      if (!state.evidenceBucket) return state;

      const { foundItem } = action.payload;

      // Mark as found in the bucket
      const evidence = state.evidenceBucket.evidence.map((item) => {
        if (item.id === foundItem.id) {
          return { ...item, found: true };
        }
        return item;
      });

      // Add to found evidence if not already found
      const alreadyFound = state.evidenceBucket.foundEvidence.some(
        (item) => item.id === foundItem.id,
      );
      const newFoundEvidence = alreadyFound
        ? state.evidenceBucket.foundEvidence
        : [...state.evidenceBucket.foundEvidence, foundItem];

      return {
        ...state,
        evidenceBucket: {
          ...state.evidenceBucket,
          evidence,
          foundEvidence: newFoundEvidence,
        },
      };
    },
    updateActiveClueIndices: (
      state,
      action: PayloadAction<{
        foundItemIndex: number;
        isActiveClue: boolean;
        unlockedSceneIds: string[];
      }>,
    ) => {
      if (!state.evidenceBucket || !state.caseData) return state;

      const { foundItemIndex, isActiveClue, unlockedSceneIds } = action.payload;
      let newActiveIndices = [...state.evidenceBucket.activeClueIndices];

      if (isActiveClue) {
        // Remove the found item's index from active clues
        newActiveIndices = newActiveIndices.filter(
          (index) => index !== foundItemIndex,
        );

        // Replenish active clues back to 3 (or as many as available)
        const maxActiveClues = 3;
        const currentLocation =
          state.caseData.locations[state.currentLocationIndex];

        while (newActiveIndices.length < maxActiveClues) {
          const nextClueIndex = findNextActiveClue(
            state.evidenceBucket.evidence,
            state.evidenceBucket.foundEvidence,
            newActiveIndices,
            foundItemIndex,
            unlockedSceneIds,
            currentLocation,
          );

          if (nextClueIndex !== null) {
            newActiveIndices.push(nextClueIndex);
          } else {
            // No more clues available, stop trying
            break;
          }
        }
      }

      return {
        ...state,
        evidenceBucket: {
          ...state.evidenceBucket,
          activeClueIndices: newActiveIndices,
        },
      };
    },

    // Location/scene management
    setSceneIndex: (state, { payload }: PayloadAction<number>) => ({
      ...state,
      currentSceneIndex: payload,
    }),
    unlockScenes: (
      state,
      action: PayloadAction<{
        newlyUnlockedScenes: string[];
      }>,
    ) => {
      if (!state.evidenceBucket) return state;

      const { newlyUnlockedScenes } = action.payload;
      const updatedUnlockedScenes =
        newlyUnlockedScenes.length > 0
          ? [...state.evidenceBucket.unlockedSceneIds, ...newlyUnlockedScenes]
          : state.evidenceBucket.unlockedSceneIds;

      return {
        ...state,
        evidenceBucket: {
          ...state.evidenceBucket,
          unlockedSceneIds: updatedUnlockedScenes,
        },
      };
    },
    setPendingSceneNavigation: (
      state,
      action: PayloadAction<{ sceneIndex: number | null }>,
    ) => {
      return {
        ...state,
        pendingSceneNavigation: action.payload.sceneIndex,
      };
    },
    handleSceneEnd: (
      state,
      {
        payload: { foundItems, wrongClicks },
      }: PayloadAction<{ foundItems: Item[]; wrongClicks: number }>,
    ) => {
      const currentLocation =
        state.caseData?.locations?.[state.currentLocationIndex];
      if (!currentLocation) return;

      const newState = {
        ...state,
        foundItems: [...state.foundItems, ...foundItems],
        wrongClicks: state.wrongClicks + wrongClicks,
        gameStatus: GameStatus.LEVEL_SUMMARY,
      };

      // Check if there are more scenes in this location
      if (state.currentSceneIndex >= currentLocation.scenes.length - 1) {
        // No more scenes in this location, check if we should end the level
        return {
          ...newState,
          gameStatus: GameStatus.LEVEL_SUMMARY,
        };
      } else {
        // Move to the next scene in this location
        return {
          ...newState,
          currentSceneIndex: state.currentSceneIndex + 1,
        };
      }
    },

    // Errors
    handleGameError: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        error: action.payload,
        gameStatus: GameStatus.ERROR,
      };
    },

    // Level End Logic
    triggerLevelEnd: (
      state,
      action: PayloadAction<{
        reason: 'evidence_complete' | 'manual_bail';
        timeRemaining: number;
        clearedByEvidence: boolean;
      }>,
    ) => {
      return {
        ...state,
        levelEndState: {
          shouldEndLevel: true,
          endReason: action.payload.reason,
          timeRemaining: action.payload.timeRemaining,
          clearedByEvidence: action.payload.clearedByEvidence,
        },
      };
    },

    clearLevelEnd: (state) => {
      return {
        ...state,
        levelEndState: {
          shouldEndLevel: false,
          endReason: null,
          timeRemaining: 0,
          clearedByEvidence: false,
        },
      };
    },

    checkLevelEndConditions: (state) => {
      if (!state.evidenceBucket || !state.caseData) return state;

      const currentLocation =
        state.caseData.locations[state.currentLocationIndex];
      if (!currentLocation) return state;

      const foundCount = state.evidenceBucket.foundEvidence.length;
      const totalCount = state.evidenceBucket.evidence.length;

      // Check if this location has a "last item" (final narrative reveal)
      // If so, level can ONLY end when that item is found
      const lastItem = state.evidenceBucket.evidence.find(
        (item) => item.isLastItem,
      );
      if (lastItem) {
        const lastItemFound = state.evidenceBucket.foundEvidence.some(
          (item) => item.isLastItem,
        );

        // Level can ONLY end when the last item is found
        if (!lastItemFound) {
          return state; // Don't end the level yet, even if all other items found
        }

        // Last item was found - end the level
        return {
          ...state,
          levelEndState: {
            shouldEndLevel: true,
            endReason: 'evidence_complete',
            timeRemaining: 0, // Will be updated by component
            clearedByEvidence: true,
          },
        };
      }

      // Level ends when ALL evidence is found
      if (foundCount >= totalCount) {
        return {
          ...state,
          levelEndState: {
            shouldEndLevel: true,
            endReason: 'evidence_complete',
            timeRemaining: 0, // Will be updated by component
            clearedByEvidence: true,
          },
        };
      }

      // Not all evidence found yet
      return state;
    },
  },
});

export const {
  resetGameState,
  initializeGame,
  initializeLocation,

  setLoadingMessage,
  setGameStatus,
  setCurrentLocationIndex,
  setSceneIndex,

  setSwipeTutorialComplete,

  setWrongClick,

  initializeLocationEvidenceBucket,
  markEvidenceAsFound,
  updateActiveClueIndices,
  unlockScenes,
  setPendingSceneNavigation,

  handleSceneEnd,

  handleGameError,

  // Level End Actions
  triggerLevelEnd,
  clearLevelEnd,
  checkLevelEndConditions,
} = gameSlice.actions;

export default gameSlice.reducer;
